<template>
  <div>
    <div class="card" v-if="project && (itemsTaken || editing)">
      <h1 class="card__title__border d-flex">
        <div class="full-width">{{ $t('messages.updates') }}</div>
        <div v-if="editing">
          <b-button variant="primary" v-b-tooltip.hover :title="$t('messages.updates_new')" @click="initializeUpdateEditor(null)">
            <font-awesome-icon icon="plus"></font-awesome-icon>
          </b-button>
        </div>
      </h1>
      <div v-if="!editing || updates.title === null">
        <div class="card__scroll" v-if="updates.data && updates.data.length > 0">
          <div v-for="update in updates.data" :key="update.id" class="card__update__item" :class="{ 'card__update__item__overflow': update.url !== null }">
            <a :href="update.url" target="_blank">
              <div>
                <div class="float-left w-75">
                  <div class="font-weight-bold text-primary" v-html="update.title"></div>
                  <div class="font-italic">{{ translateDate(update.created_date) }}</div>
                </div>
                <div class="float-right" v-if="update.url">
                  <font-awesome-icon v-if="project.source === 'KICKSTARTER'" :icon="['fab', 'kickstarter']" class="red" size="2x"></font-awesome-icon>
                  <font-awesome-icon v-if="project.source !== 'KICKSTARTER'" icon="external-link-square-alt" class="red" size="2x"></font-awesome-icon>
                </div>
                <div class="float-right d-flex" v-if="editing && !update.url">
                  <b-button v-b-tooltip.hover :title="$t('messages.updates_edit')" variant="transparent" @click="initializeUpdateEditor(update)" class="mr-1 bg-transparent p-0" size="sm">
                    <font-awesome-icon icon="pencil-alt" class="red"></font-awesome-icon>
                  </b-button>
                  <click-confirm button-size="sm" :messages="confirmMessage">
                    <b-button v-b-tooltip.hover :title="$t('messages.updates_delete')" variant="transparent" @click="deleteUpdate(update)" class="bg-transparent p-0 mt-m-1" size="sm">
                      <font-awesome-icon icon="trash-alt" class="red"></font-awesome-icon>
                    </b-button>
                  </click-confirm>
                </div>
                <div class="clear"></div>
                <div class="pt-3" v-html="update.description"></div>
              </div>
              <div class="card__update__item__gradient"></div>
            </a>
          </div>
        </div>
        <div v-if="!updates.data || updates.data.length === 0" class="card__update__item font-italic">
          {{ $t('messages.updates_empty') }}
        </div>
      </div>
      <div v-if="editing && updates.title !== null" class="p-20">
        <b-form @submit.prevent="saveUpdate">
          <b-form-group id="updateTitleGroup"
                        :label="$t('messages.title')+':'"
                        label-for="updateTitle"
                        :invalid-feedback="$t('errors.title')">
            <b-form-input id="updateTitle" name="updateTitle"
                          v-model="updates.title"
                          @input="$v.updates.title.$touch"
                          :state="$v.updates.title.$dirty ? !$v.updates.title.$error : null"></b-form-input>
          </b-form-group>
          <b-form-group id="updateContentGroup"
                        :label="$t('messages.content')+':'"
                        label-for="updateContent"
                        :invalid-feedback="$t('errors.content')">
            <ckeditor id="updateContent" name="updateContent" :editor="updates.editor.instance" v-model="updates.editor.data"
                      :config="updates.editor.config" :key="updates.editor.update"
                      @input="$v.updates.editor.data.$touch"></ckeditor>
          </b-form-group>
          <div class="d-flex">
            <notification-button :message="updates.id ? $t('messages.edit') : $t('messages.send')" :notification="notifications.update" field="update" :instant="true"
                                 :disabled="$v.updates.$invalid" class="mr-2"></notification-button>
            <b-button variant="primary" @click="clearUpdate">{{ $t('messages.cancel') }}</b-button>
          </div>
        </b-form>
      </div>
    </div>
  </div>
</template>

<script>
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'

import { projectService } from '../../services/project.service'
import NotificationButton from '../NotificationButton'

import { defaultMixin } from '../../mixin/default'
import { notificationMixin } from '../../mixin/notification'
import { validationMixin } from 'vuelidate'

import { required } from 'vuelidate/lib/validators'

export default {
  name: 'update',
  mixins: [ defaultMixin, notificationMixin, validationMixin ],
  components: { NotificationButton },
  props: {
    value: Object,
    editing: Boolean,
    itemsTaken: Boolean
  },
  data: function () {
    return {
      project: this.value,
      updates: {
        data: [],
        id: null,
        title: null,
        editor: {
          instance: ClassicEditor,
          data: '',
          config: {
            toolbar: [ 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', 'insertTable', 'undo', 'redo' ],
            language: this.$i18n.locale
          },
          update: 0
        }
      },
      notifications: {
        update: null
      }
    }
  },
  validations: function () {
    return {
      updates: {
        title: {
          required
        },
        editor: {
          data: {
            required
          }
        }
      }
    }
  },
  methods: {
    fetchUpdates: function () {
      let store = this.$store
      projectService.fetchUpdates(this.project.id)
        .then((updates) => {
          this.updates.data = updates
        }, () => {
          store.commit('error', 'errors.project')
        })
    },
    clearUpdate: function () {
      this.updates.id = null
      this.updates.title = null
      this.updates.editor.data = ''
    },
    saveUpdate: function () {
      projectService.postUpdate(this.project.id, { 'id': this.updates.id, 'title': this.updates.title, 'description': this.updates.editor.data })
        .then(() => {
          if (this.updates.id) {
            this.$store.commit('success', 'success.update_edit')
          } else {
            this.$store.commit('success', 'success.update_add')
          }
          this.clearUpdate()
          this.fetchUpdates()
        }, () => {
          if (this.updates.id) {
            this.$store.commit('error', 'errors.update_edit')
          } else {
            this.$store.commit('error', 'errors.update_add')
          }
          this.sendNotification('update', 'error')
        })
    },
    deleteUpdate: function (update) {
      projectService.deleteUpdate(update.id)
        .then(() => {
          this.$store.commit('success', 'success.update_delete')
          this.fetchUpdates()
        }, () => this.$store.commit('error', 'errors.update_delete'))
    },
    initializeUpdateEditor: function (update) {
      this.$v.updates.$reset()

      if (update !== null) {
        this.updates.id = update.id
        this.updates.title = update.title
        this.updates.editor.data = update.description
        this.$v.updates.$touch()
      } else {
        this.updates.id = null
        this.updates.title = ''
        this.updates.editor.data = ''
      }
    }
  },
  created: function () {
    this.fetchUpdates()
    this.$root.$on('updateLocale', () => {
      this.updates.editor.config.language = this.$i18n.locale
      this.updates.editor.update += 1
    })
    this.$root.$on('resetNotification', (notification) => {
      this.resetNotification(notification)
    })
  }
}
</script>

<style scoped>
  .card__update__item {
    padding: 20px;
    border-bottom: 1px #D3D3D3 solid;
  }

  .card__update__item__overflow {
    max-height: 400px;
    overflow: hidden;
    position: relative;
  }

  .card__update__item__overflow .card__update__item__gradient {
    position: absolute;
    bottom: 0px;
    left: 0px;
    width: 100%;
    height: 50px;
    background: -moz-linear-gradient(top, rgba(0, 0, 0, 0) 0%, rgba(255, 255, 255, 1) 90%); /* FF3.6+ */
    background: -webkit-gradient(linear, left top, left bottom, color-stop(0%, rgba(0, 0, 0, 0)), color-stop(90%, rgba(255, 255, 255, 1))); /* Chrome,Safari4+ */
    background: -webkit-linear-gradient(top, rgba(0, 0, 0, 0) 0%, rgba(255, 255, 255, 1) 90%); /* Chrome10+,Safari5.1+ */
    background: -o-linear-gradient(top, rgba(0, 0, 0, 0) 0%, rgba(255, 255, 255, 1) 90%); /* Opera 11.10+ */
    background: -ms-linear-gradient(top, rgba(0, 0, 0, 0) 0%, rgba(255, 255, 255, 1) 90%); /* IE10+ */
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(255, 255, 255, 1) 90%); /* W3C */
    filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#0089fff1', endColorstr='#000000',GradientType=0 ); /* IE6-9 */
  }

  .card__update__item a {
    text-decoration: none;
    color: inherit;
  }
</style>



// WEBPACK FOOTER //
// src/components/project/Update.vue